import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useUi, useTabs, useAllTabs, useCurrentWorkspaceId } from "@/lib/store";
import { useWorkspaces } from "@/lib/queries";
import type { User } from "@/lib/types";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Rail } from "./Rail";
import { WorkspaceBrowser } from "./WorkspaceBrowser";
import { TabBar } from "./TabBar";
import { TopBar } from "./TopBar";
import { ViewHost } from "./ViewHost";
import { RightPanel } from "./RightPanel";
import { SearchDialog } from "./SearchDialog";
import { NewNoteDialog } from "./NewNoteDialog";
import { useGlobalShortcuts } from "./shortcuts";
import { forgetDeletedNotes, peekEntry, retainOnly } from "@/features/notes/collab";
import { useIsUnlocked } from "@/features/crypto/keyring";
import { LockedProjectSidebar } from "@/features/crypto/ProjectGate";
import { useBillingReturn } from "@/features/settings/PlanSettings";
import { track } from "@/lib/analytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const SettingsView = lazy(() => import("@/features/settings/SettingsView").then((m) => ({ default: m.SettingsView })));
const BacklogView = lazy(() => import("@/features/tasks/BacklogView").then((m) => ({ default: m.BacklogView })));
const FilesView = lazy(() => import("@/features/files/FilesView").then((m) => ({ default: m.FilesView })));
const ReviewView = lazy(() => import("@/features/overview/ReviewView").then((m) => ({ default: m.ReviewView })));
const ArchiveView = lazy(() => import("@/features/archive/ArchiveView").then((m) => ({ default: m.ArchiveView })));

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

export function Shell({ user }: { user: User }) {
  useBillingReturn();
  const workspaces = useWorkspaces();
  const wsId = useCurrentWorkspaceId();
  const setWorkspace = useUi((s) => s.setWorkspace);
  const railCollapsed = useUi((s) => s.railCollapsed);
  const browserCollapsed = useUi((s) => s.browserCollapsed);
  const focusMode = useUi((s) => s.focusMode);
  const rightPanel = useUi((s) => s.rightPanel);
  const setRightPanel = useUi((s) => s.setRightPanel);
  const mobileNavOpen = useUi((s) => s.mobileNavOpen);
  const setMobileNav = useUi((s) => s.setMobileNav);
  const { tabs, activeKey } = useTabs();
  const everyTab = useAllTabs();
  const page = useUi((s) => s.page);
  const narrow = useIsNarrow();
  const [searchOpen, setSearchOpen] = useState(false);
  const [newNoteOpen, setNewNoteOpen] = useState(false);
  const currentUnlocked = useIsUnlocked(workspaces.data?.find((w) => w.id === wsId)?.encryptionScopeId);

  useGlobalShortcuts({ onSearch: () => setSearchOpen(true), onNewNote: () => setNewNoteOpen(true) });

  // Default to the first notebook; recover if the remembered one is no longer accessible.
  const current = useMemo(() => workspaces.data?.find((w) => w.id === wsId), [workspaces.data, wsId]);
  useEffect(() => {
    if (!workspaces.data?.length) return;
    if (!current) {
      // The person's own first notebook (their personal one), else any notebook or project.
      // Never an archived one (they are opened from the Archive).
      const active = workspaces.data.filter((w) => !w.archivedAt);
      const own = active.filter((w) => w.kind === "notebook" && w.ownerId === user.id).sort((a, b) => String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? "")));
      const next = own[0] ?? active.find((w) => w.kind === "notebook") ?? active[0] ?? workspaces.data[0];
      // Keep a full page (Settings, My tasks) open while switching underneath it.
      const keepPage = useUi.getState().page;
      setWorkspace(next.id);
      if (keepPage) useUi.getState().openPage(keepPage.name, keepPage.params);
    }
  }, [workspaces.data, current, setWorkspace, user.id]);

  // Notebooks/projects that were deleted or unshared: forget their tabs, and the offline copies of
  // those notes once the server confirms they are gone for this person (never with unsynced edits).
  useEffect(() => {
    if (!workspaces.data) return;
    const ui = useUi.getState();
    const buckets = ui.userId ? ui.tabsByUser[ui.userId] : undefined;
    if (!buckets) return;
    const live = new Set(workspaces.data.map((w) => w.id));
    for (const [wsKey, bucket] of Object.entries(buckets)) {
      if (live.has(wsKey)) continue;
      const noteIds = bucket.tabs.filter((t) => t.kind === "note" && t.id).map((t) => t.id!);
      ui.dropWorkspaceTabs(wsKey);
      void (async () => {
        const gone: string[] = [];
        for (const id of noteIds) {
          if (peekEntry(id)?.hasUnsynced()) continue;
          const res = await fetch(`/api/notes/${id}`, { credentials: "same-origin" }).catch(() => null);
          if (res?.status === 404) gone.push(id);
        }
        if (gone.length) await forgetDeletedNotes(user.id, gone);
      })().catch(() => undefined);
    }
  }, [workspaces.data, user.id]);

  // Keep live document connections only for notes open in a tab (in any notebook or project).
  useEffect(() => {
    retainOnly(new Set(everyTab.filter((t) => t.kind === "note" && t.id).map((t) => t.id!)));
  }, [everyTab]);

  const activeTab = tabs.find((t) => t.key === activeKey) ?? null;
  // Which parts of the app are used (counted only with the person's consent): the kind of screen, never its content.
  const screen = page ? `/app/${page.name}${page.name === "settings" && page.params?.tab ? `/${page.params.tab}` : ""}` : activeTab ? `/app/${activeTab.kind === "view" ? activeTab.view : activeTab.kind}` : "/app";
  useEffect(() => track("page_view", undefined, screen), [screen]);
  const currentLocked = (!!current?.encryptionScopeId && !currentUnlocked) || !!current?.legacyLocked;
  const showSidebars = !focusMode;

  const nav = (
    <div className="flex h-full min-h-0">
      <Rail user={user} collapsed={narrow ? true : railCollapsed} narrow={narrow} />
      {current && !page && (!browserCollapsed || narrow) && (currentLocked ? <LockedProjectSidebar id={current.id} name={current.name} kind={current.kind} /> : <WorkspaceBrowser workspace={current} onNewNote={() => setNewNoteOpen(true)} />)}
    </div>
  );

  return (
    // relative: hidden helper labels (sr-only) are positioned inside the app, not on the page around it.
    <div className="relative flex h-full min-h-0 overflow-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-card focus:p-2">
        Skip to document
      </a>
      {!narrow && showSidebars && <nav aria-label="Workspaces and notes" className="flex h-full min-h-0 shrink-0">{nav}</nav>}
      {narrow && (
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNav}>
          <SheetContent side="left" className="w-[min(22rem,92vw)] gap-0 p-0 sm:max-w-none">
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation</SheetTitle>
              <SheetDescription>Notebooks, projects, folders and notes</SheetDescription>
            </SheetHeader>
            {nav}
          </SheetContent>
        </Sheet>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar user={user} activeTab={page ? null : activeTab} page={page?.name} narrow={narrow} workspace={current} onSearch={() => setSearchOpen(true)} onNewNote={() => setNewNoteOpen(true)} />
        {!page && <TabBar narrow={narrow} />}
        <div className="flex min-h-0 flex-1">
          <main id="main-content" className={cn("min-w-0 flex-1 overflow-hidden")} tabIndex={-1}>
            {page ? (
              <ErrorBoundary key={`page:${page.name}:${JSON.stringify(page.params ?? {})}`}>
                <Suspense fallback={<p className="p-6 text-muted-foreground" role="status">Loading…</p>}>
                  <div className="h-full min-h-0 overflow-hidden">
                    {page.name === "settings" ? (
                      <SettingsView params={page.params} />
                    ) : page.name === "backlog" ? (
                      <BacklogView crossProject />
                    ) : page.name === "files" ? (
                      <FilesView />
                    ) : page.name === "archive" ? (
                      <ArchiveView />
                    ) : (
                      <ReviewView />
                    )}
                  </div>
                </Suspense>
              </ErrorBoundary>
            ) : (
              <ErrorBoundary>
                <ViewHost tab={activeTab} workspace={current} />
              </ErrorBoundary>
            )}
          </main>
          {!narrow && rightPanel && !focusMode && !page && (
            <aside className="w-[22rem] shrink-0 border-l bg-sidebar" aria-label="Side panel">
              <RightPanel tab={activeTab} />
            </aside>
          )}
          {narrow && !page && (
            <Sheet open={!!rightPanel} onOpenChange={(o) => !o && setRightPanel(null)}>
              <SheetContent side="right" className="w-[min(24rem,95vw)] p-0 sm:max-w-none">
                <SheetHeader className="sr-only">
                  <SheetTitle>Side panel</SheetTitle>
                  <SheetDescription>Sources, comments or assistant</SheetDescription>
                </SheetHeader>
                <RightPanel tab={activeTab} />
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} workspace={current} />
      {current && <NewNoteDialog open={newNoteOpen} onOpenChange={setNewNoteOpen} workspace={current} />}
    </div>
  );
}
